import * as Dfns from 'date-fns/fp';
import { Flight, FlightChange, Order } from './streaming-buying-types';

export const MEDIA_OPS_EMAIL = 'bpm-media-buying@tinuiti.com';

// Where a send started, as bpm-lambdas keeps it on an agent order's send record.
export const SendVia = {
  PLAN_BUY: 'plan_buy',
  AGENT_ORDER_APPROVALS: 'agent_order_approvals'
} as const;
export type SendViaType = (typeof SendVia)[keyof typeof SendVia];

// The IO's key in bpm-streaming-orders: bpm-lambdas holds an agent order's IO to order<id>_….pdf.
// It's new each time, so polling for it can't find an earlier IO.
export const orderPdfFilename = (id: number, now: Date): string =>
  `order${id}_${Dfns.format('yyyy-MM-dd_HH-mm-ss', now)}.pdf`;

const PPM_ADDENDUM =
  "\n\nProspect Point Media (PPM) is a sister agency to Bliss Point Media (BPM), functioning as a wholly owned subsidiary. Media spend should be applied toward BPM's total overall commitments, using the same media rates previously negotiated in BPM contracts.\n\nBilling Information:\nTo ensure prompt reconciliation of bills and payment, please address bills as follows:\nPrimary AP Contact: accounting@prospectpointmedia.com\n\nMailing Address:\nProspect Point Media\n1507 7th St. Suite 132\nSanta Monica, CA 90401";

const TINUITI_ADDENDUM =
  "\n\nTinuiti is a sister agency to Bliss Point Media (BPM). Media spend should be applied toward BPM's total overall commitments, using the same media rates previously negotiated in BPM contracts.";

const agencyAddendum = (agency: string): string => {
  if (agency === 'ppm') return PPM_ADDENDUM;
  if (agency === 'tinuiti') return TINUITI_ADDENDUM;
  return '';
};

export const makeDefaultEmailBodyText = ({
  name,
  company,
  agency
}: {
  name: string;
  company: string;
  agency: string;
}): string =>
  `Hi ${name},\n\nAttached is our streaming/FEP order for ${company}. Please review and send back a signed confirmation. *Existing tags will be extended for the duration of this flight*.\n\nThanks!${agencyAddendum(agency)}`;

export const orderEmailSubject = ({
  order,
  isNewOrder,
  companyName,
  network
}: {
  order: Order;
  isNewOrder: boolean;
  companyName: string;
  network: string;
}): string => {
  if (isNewOrder || (order.status === 0 && order.revision === 1)) {
    return `New Streaming/FEP Order for ${companyName} - ${network}`;
  }
  return `Streaming/FEP Order Revision (#${order.id}, version ${order.revision}) for ${companyName} - ${network}`;
};

// The properties the order books, unsaved flight edits included; the network's contacts are per
// property.
export const orderPlatforms = (
  flights: Flight[],
  flightChangeMap: Record<string, FlightChange> = {}
): string[] => {
  const platforms = new Set<string>();
  for (const flight of flights) {
    const combo = { ...flight, ...(flightChangeMap[flight.id] || {}) };
    if (combo.platform) {
      platforms.add(combo.platform);
    }
  }
  for (const flight of Object.values(flightChangeMap)) {
    if (flight.platform) {
      platforms.add(flight.platform);
    }
  }
  return [...platforms];
};

// The body's *bold* and _italics_ (see MailComposer's hint) as the mailer's HTML.
export const toEmailHtml = (bodyText: string): string =>
  bodyText
    .replace(/\n/g, '<br />')
    .replace(/\*(.+?)\*/g, '<strong>$1</strong>')
    .replace(/_(.+?)_/g, '<em>$1</em>');
